import React, { useMemo, useState } from "react";
import { ArrowRight, Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import { formatINR } from "../lib/pricing";
import { useStudio } from "../context/StudioContext";
import { usePlanDrawer } from "../context/PlanDrawerContext";

export default function UnitRateCard({ service, sub }) {
  const { addToPlan } = useStudio();
  const { openDrawer } = usePlanDrawer();

  const step = sub.step || 1;
  const min = sub.minQuantity || 1;
  const max = sub.maxQuantity || 100000;
  const [qty, setQty] = useState(sub.defaultQuantity || min);

  const clamp = (n) => Math.max(min, Math.min(max, n));

  const subtotal = useMemo(() => sub.unitRate * qty, [sub.unitRate, qty]);
  const gst = subtotal * 0.18;
  const total = subtotal + gst;

  const add = () => {
    const key = `${sub.code}:qty:${qty}`;
    addToPlan({
      key,
      code: sub.code,
      title: `${sub.title} (${qty.toLocaleString("en-IN")} ${sub.unitLabel}${qty > 1 ? "s" : ""})`,
      planName: `${qty.toLocaleString("en-IN")} \u00d7 ${sub.unitLabel}`,
      service: service.label,
      price: formatINR(subtotal),
      billing: sub.billing,
      serviceSlug: service.slug,
      subSlug: sub.slug,
    });
    toast.success(`${sub.title} added to your plan.`);
    openDrawer();
  };

  return (
    <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-[1fr_400px]">
      <div className="rounded-xl border border-brand-hair bg-brand-panel p-6">
        <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
          What's included
        </div>
        <ul className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
          {(sub.included || []).map((item, i) => (
            <li key={i} className="flex items-start gap-3 text-[13.5px] text-brand-ivory/85">
              <span className="mt-[6px] flex h-1.5 w-1.5 flex-none rounded-full bg-brand" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="sticky top-4 rounded-xl border border-brand-hair bg-brand-panel p-6">
        <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
          Unit rate
        </div>
        <div className="mt-2 flex items-baseline gap-2">
          <div className="font-serif-display text-[40px] leading-none text-brand-ivory">
            {formatINR(sub.unitRate)}
          </div>
          <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-brand-mute">
            per {sub.unitLabel}
          </div>
        </div>

        <div className="mt-6 font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
          Quantity ({sub.unitLabel}s)
        </div>
        <div className="mt-2 flex items-center gap-2">
          <button
            onClick={() => setQty((q) => clamp(q - step))}
            className="flex h-11 w-11 items-center justify-center rounded-md border border-brand-hair text-brand-ivory hover:border-brand hover:text-brand"
            aria-label="Decrease"
          >
            <Minus className="h-4 w-4" />
          </button>
          <input
            type="number"
            min={min}
            max={max}
            step={step}
            value={qty}
            onChange={(e) => setQty(clamp(parseInt(e.target.value || min, 10) || min))}
            className="h-11 flex-1 rounded-md border border-brand-hair bg-brand-panel2 px-3 text-center font-serif-display text-[20px] text-brand-ivory focus:border-brand"
          />
          <button
            onClick={() => setQty((q) => clamp(q + step))}
            className="flex h-11 w-11 items-center justify-center rounded-md border border-brand-hair text-brand-ivory hover:border-brand hover:text-brand"
            aria-label="Increase"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>
        <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.22em] text-brand-mute">
          Min {min.toLocaleString("en-IN")} &mdash; max {max.toLocaleString("en-IN")}
        </div>

        <div className="mt-6 space-y-2 rounded-md border border-brand-hair bg-brand-panel2 p-4 text-[13px]">
          <Row label={`Subtotal (${qty.toLocaleString("en-IN")} \u00d7 ${formatINR(sub.unitRate)})`} value={formatINR(subtotal)} />
          <Row label="GST @ 18%" value={formatINR(gst)} muted />
          <div className="my-2 h-px bg-brand-hair" />
          <div className="flex items-baseline justify-between">
            <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
              Total
            </div>
            <div className="font-serif-display text-[24px] text-brand">{formatINR(total)}</div>
          </div>
        </div>

        <button
          onClick={add}
          className="group mt-5 flex w-full items-center justify-center gap-3 rounded-md bg-brand py-3.5 font-mono text-[12px] font-semibold uppercase tracking-[0.28em] text-white transition-colors hover:bg-[#ff5c26]"
        >
          Add to plan
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </button>
      </div>
    </div>
  );
}

function Row({ label, value, muted }) {
  return (
    <div className={`flex items-baseline justify-between ${muted ? "text-brand-ivory/55" : "text-brand-ivory/85"}`}>
      <div className="text-[12.5px]">{label}</div>
      <div className="font-mono text-[12.5px]">{value}</div>
    </div>
  );
}
